import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { CustomChemicalService, CustomChemical } from '../../../../../core/services/custom-chemical.service';

@Component({
  selector: 'app-custom-chemical-details',
  standalone: true,
  imports: [CommonModule, RouterLink, ConfirmModalComponent],
  templateUrl: './custom-chemical-details.component.html',
  styleUrls: ['./custom-chemical-details.component.scss']
})
export class CustomChemicalDetailsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private customChemicalService = inject(CustomChemicalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // State
  isLoading = true;
  errorMessage: string | null = null;
  chemicalId: string | null = null;
  chemical: CustomChemical | null = null;

  ngOnInit(): void {
    this.chemicalId = this.route.snapshot.paramMap.get('id');
    if (this.chemicalId) {
      this.loadChemical(this.chemicalId);
    } else {
      this.errorMessage = 'No chemical ID provided';
      this.isLoading = false;
    }
  }

  private async loadChemical(id: string): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const chemical = await this.customChemicalService.getChemicalById(id);
      if (chemical) {
        this.chemical = chemical;
      } else {
        this.errorMessage = 'Chemical not found';
      }
    } catch (error) {
      console.error('Error loading chemical:', error);
      this.errorMessage = 'Failed to load chemical details';
    } finally {
      this.isLoading = false;
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/logbook/chemicals/custom']);
  }

  editChemical(): void {
    if (this.chemicalId) {
      this.router.navigate(['/logbook/chemicals/custom', this.chemicalId, 'edit']);
    }
  }

  duplicateChemical(): void {
    // Placeholder - will be implemented later
    console.log('Duplicate functionality coming soon');
  }

  // Delete functionality
  confirmDelete(): void {
    if (this.chemical) {
      this.confirmModal.open({
        title: 'Delete Custom Chemical',
        message: `Are you sure you want to delete "${this.chemical.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonClass: 'btn-danger',
        icon: 'bi-trash'
      });
    }
  }

  async onDeleteConfirmed(): Promise<void> {
    if (this.chemical) {
      const success = await this.customChemicalService.deleteChemical(this.chemical.id);
      if (success) {
        console.log('Chemical deleted successfully');
        this.router.navigate(['/logbook/chemicals/custom']);
      } else {
        this.errorMessage = 'Failed to delete chemical';
      }
    }
  }

  onDeleteCancelled(): void {
    // No action needed
  }

  // Category badge helpers (matching backend categories)
  getCategoryBadgeClass(category?: string): string {
    switch (category) {
      case 'herbicide':
        return 'bg-success-subtle text-success';
      case 'insecticide':
        return 'bg-danger-subtle text-danger';
      case 'fungicide':
        return 'bg-primary-subtle text-primary';
      case 'fertilizer':
        return 'bg-warning-subtle text-warning-emphasis';
      case 'other':
        return 'bg-secondary-subtle text-secondary';
      default:
        return 'bg-secondary';
    }
  }

  getCategoryLabel(category?: string): string {
    if (!category) return 'Other';
    return category.charAt(0).toUpperCase() + category.slice(1);
  }

  // TrackBy function
  trackByIndex(index: number): number {
    return index;
  }
}
